import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useAuth } from '../../../contexts/AuthContext';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import {
  surgeryService,
  type SurgicalProcedure,
  STATUS_BADGE,
  STATUS_LABELS,
  PROCEDURE_TYPE_LABELS,
} from '../surgeryService';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Spinner from '../../../components/ui/Spinner';
import { parseApiDate } from '../../../lib/format';

const SurgicalProcedureListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const doctorId = user?.doctor?.id;

  const [procedures, setProcedures] = useState<SurgicalProcedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await surgeryService.getAll({
        page: p,
        per_page: 15,
        doctor_id: doctorId,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setProcedures(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Error loading procedures:', err);
    } finally {
      setLoading(false);
    }
  }, [doctorId, search, statusFilter]);

  useEffect(() => { load(1); setPage(1); }, [load]);

  const requestDelete = (id: number) => {
    setPendingDeleteId(id);
  };

  const cancelDelete = () => {
    setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await surgeryService.delete(id);
      load(page);
    } catch { /* handled by API */ }
  };

  const formatDate = (d: string) =>
    d ? (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Procedimientos Quirúrgicos</h3>
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Cirugías' }]} />
          </div>
          <div className="col-auto">
            <Link to="/surgery/new" className="btn btn-primary">
              <i className="fa fa-plus me-2"></i>Nuevo Procedimiento
            </Link>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="row align-items-center g-2">
            <div className="col-md-5">
              <div className="input-group">
                <span className="input-group-text"><i className="fa fa-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por paciente o procedimiento..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">Todos los estados</option>
                <option value="scheduled">Programada</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
            <div className="col-auto ms-auto text-muted">
              {total} registro{total !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Paciente</th>
                  <th>Procedimiento</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-4"><Spinner /></td></tr>
                ) : procedures.length === 0 ? (
                  <EmptyState variant="compact" icon="fa-kit-medical" title="No hay procedimientos registrados" action={{ label: 'Registrar procedimiento', to: '/surgery/new', icon: 'fa-plus' }} />
                ) : (
                  procedures.map(proc => (
                    <tr key={proc.id}>
                      <td>
                        <strong>{proc.patient?.first_name} {proc.patient?.last_name}</strong>
                        {proc.patient?.identification && (
                          <div><small className="text-muted">{proc.patient.identification}</small></div>
                        )}
                      </td>
                      <td>{proc.procedure_name}</td>
                      <td><small className="text-muted">{PROCEDURE_TYPE_LABELS[proc.procedure_type] ?? proc.procedure_type}</small></td>
                      <td>{formatDate(proc.procedure_date)}</td>
                      <td>
                        <span className={STATUS_BADGE[proc.status] ?? 'custom-badge status-grey'}>
                          {STATUS_LABELS[proc.status] ?? proc.status}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => navigate(`/surgery/${proc.id}`)}
                          aria-label="Ver detalle"
                        >
                          <FeatherIcon icon="eye" size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => navigate(`/surgery/${proc.id}/edit`)}
                          aria-label="Editar"
                        >
                          <FeatherIcon icon="edit-2" size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => requestDelete(proc.id)}
                          aria-label="Eliminar"
                        >
                          <FeatherIcon icon="trash-2" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {total > 15 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <small className="text-muted">Página {page}</small>
            <div>
              <button className="btn btn-sm btn-outline-secondary me-1" disabled={page <= 1} onClick={() => { setPage(p => p - 1); load(page - 1); }}>
                <FeatherIcon icon="chevron-left" size={14} />
              </button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page * 15 >= total} onClick={() => { setPage(p => p + 1); load(page + 1); }}>
                <FeatherIcon icon="chevron-right" size={14} />
              </button>
            </div>
          </div>
        )}

      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar procedimiento"
        body="¿Eliminar este procedimiento quirúrgico? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default SurgicalProcedureListPage;
